import { useState, useEffect } from "react";
import { DataTable } from "@/components/ui/data-table";
import { CouponTableSkeleton } from "@/components/ui/coupon-table-skeleton";
import { Button } from "@/components/ui/button";
import { Plus, Eye, ExternalLink } from "lucide-react";
import {
  SlotConfigurationApi,
  SlotConfiguration,
} from "@/api/giftcard.api";
import { HandleError } from "@/api/axiosClient";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { getFormattedDateTime } from "@/utils";
import { getSlotSymbolById } from "./dummyData";

export function GamificationAllSlotMachines() {
  const [slotMachines, setSlotMachines] = useState<SlotConfiguration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedSlotMachine, setSelectedSlotMachine] =
    useState<SlotConfiguration | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchSlotMachines();
  }, []);

  const fetchSlotMachines = async () => {
    setIsLoading(true);
    try {
      const response = await SlotConfigurationApi.getAllSlotConfiguration({
        p_PageNumber: 1,
        p_PageSize: 3,
      });

      if (response.error_msg && response.error_msg.trim() !== "") {
        toast({
          title: "Error",
          description: response.error_msg,
          variant: "destructive",
        });
        setSlotMachines([]);
      } else {
        setSlotMachines(response.getAllSlotConfig || []);
      }
    } catch (error: unknown) {
      console.error("Error fetching slot machines:", error);
      const errorMessage = HandleError(error);
      toast({
        title: "Error",
        description: errorMessage || "Failed to fetch slot machines",
        variant: "destructive",
      });
      setSlotMachines([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString || dateString === "0001-01-01T00:00:00") return "-";
    return getFormattedDateTime(dateString) || "-";
  };

  const isActiveSlot = (machine: SlotConfiguration) => {
    const now = Date.now();
    const start = new Date(machine.startDate).getTime();
    const end = new Date(machine.endDate).getTime();
    if (Number.isNaN(start) || Number.isNaN(end)) return false;
    return now >= start && now <= end;
  };

  const handleView = (machine: SlotConfiguration) => {
    setSelectedSlotMachine(machine);
    setIsViewModalOpen(true);
  };

  if (isLoading) {
    return <CouponTableSkeleton />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-800">
            All Slot Machines
          </h2>
          <p className="text-sm text-muted-foreground">
            View and manage all slot machine configurations
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" asChild>
            <a href="/slot-machine" target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4" />
              Public Page
            </a>
          </Button>
          <Button
            type="button"
            className="flex items-center gap-2 bg-orange-600 text-white hover:bg-orange-700"
          >
            <Plus className="h-4 w-4" />
            Create Slot Machine
          </Button>
        </div>
      </div>

      <DataTable
        title=""
        data={slotMachines}
        cellClassName="py-6 text-gray-700"
        columns={[
          {
            header: "Name",
            accessorKey: "name" as keyof SlotConfiguration,
          },
          {
            header: "Start Date",
            accessorKey: "startDate" as keyof SlotConfiguration,
            cell: (row: SlotConfiguration) => formatDate(row.startDate),
          },
          {
            header: "End Date",
            accessorKey: "endDate" as keyof SlotConfiguration,
            cell: (row: SlotConfiguration) => formatDate(row.endDate),
          },
          {
            header: "Winning Probability",
            accessorKey: "winningProbability" as keyof SlotConfiguration,
            cell: (row: SlotConfiguration) => `${row.winningProbability}%`,
          },
          {
            header: "Rules",
            id: "rules",
            cell: (row: SlotConfiguration) =>
              (row.slotRules?.length || 0).toString(),
          },
          {
            header: "Status",
            id: "status",
            cell: (row: SlotConfiguration) => {
              const active = isActiveSlot(row);
              return (
                <Badge
                  variant={active ? "default" : "secondary"}
                  className="flex items-center gap-1 w-fit"
                >
                  {active ? "Active" : "Inactive"}
                </Badge>
              );
            },
          },
          {
            header: "Action",
            id: "action",
            cell: (row: SlotConfiguration) => (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleView(row)}
                className="flex items-center gap-2"
              >
                <Eye className="h-4 w-4" />
                View
              </Button>
            ),
          },
        ]}
      />

      <Dialog open={isViewModalOpen} onOpenChange={setIsViewModalOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Slot Machine Details</DialogTitle>
          </DialogHeader>
          {selectedSlotMachine && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">ID</label>
                  <p className="text-sm text-gray-900 mt-1">
                    {selectedSlotMachine.id}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Name
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    {selectedSlotMachine.name}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Start Date
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    {formatDate(selectedSlotMachine.startDate)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    End Date
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    {formatDate(selectedSlotMachine.endDate)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Winning Probability
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    {selectedSlotMachine.winningProbability}%
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Status
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    <Badge
                      variant={
                        isActiveSlot(selectedSlotMachine)
                          ? "default"
                          : "secondary"
                      }
                    >
                      {isActiveSlot(selectedSlotMachine)
                        ? "Active"
                        : "Inactive"}
                    </Badge>
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Created
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    {formatDate(selectedSlotMachine.createdDateTime)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    Updated
                  </label>
                  <p className="text-sm text-gray-900 mt-1">
                    {formatDate(selectedSlotMachine.updatedDateTime)}
                  </p>
                </div>
              </div>
              <div className="mt-4">
                <label className="text-sm font-medium text-gray-500 mb-2 block">
                  Slot Rules
                </label>
                <div className="space-y-2">
                  {(selectedSlotMachine.slotRules || []).length === 0 ? (
                    <p className="text-sm text-gray-500">No rules configured</p>
                  ) : (
                    selectedSlotMachine.slotRules.map((rule) => {
                      const symbol = getSlotSymbolById(rule.symbolId);
                      return (
                        <div key={rule.id} className="p-3 border rounded-md">
                          <div className="flex flex-wrap justify-between items-center gap-2">
                            <span className="text-sm font-medium text-gray-700">
                              {symbol.emoji} {symbol.name}
                              <span className="text-gray-400 font-normal ml-1">
                                (ID {rule.symbolId})
                              </span>
                            </span>
                            <div className="flex gap-2">
                              <Badge variant="outline">
                                Occurance: {rule.occurance}
                              </Badge>
                              <Badge variant="default">
                                {rule.pointToWin} points
                              </Badge>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsViewModalOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
